<template>
  <div class="sidebar" :class="{ collapsed: collapsed }">
    <!-- Logo区域 -->
    <div class="sidebar-logo">
      <div v-if="logoLoading" class="logo-loading">
        <el-icon class="is-loading"><Loading /></el-icon>
      </div>
      <img v-else-if="companyLogo" :src="companyLogo" alt="Logo" :class="collapsed ? 'logo-icon' : 'logo-img'" />
    </div>
    
    <!-- 菜单列表（可滚动） -->
    <nav class="sidebar-menu">
      <router-link
        v-for="item in menuItems"
        :key="item.path"
        :to="item.path"
        class="menu-item"
        :class="{ active: isActive(item.path) }"
      >
        <el-icon class="menu-icon">
          <component :is="item.icon" />
        </el-icon>
        <span v-if="!collapsed" class="menu-title">{{ $t(`menu.${item.key}`) }}</span>
      </router-link>
    </nav>
    <!-- 底部固定：一键更新（已注释）
    <div class="sidebar-bottom">
      <button type="button" class="menu-item update-btn" :disabled="updateChecking || updateTriggering" @click="onOneClickUpdate">
        <el-icon class="menu-icon"><Upload /></el-icon>
        <span v-if="!collapsed" class="menu-title">{{ updateChecking || updateTriggering ? $t('update.checkingOrRunning') : $t('menu.oneClickUpdate') }}</span>
      </button>
    </div>
    -->
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import {
  DataLine,
  Setting,
  ChatLineRound,
  FolderOpened,
  User,
  EditPen,
  PriceTag,
  Connection,
  Loading,
  Upload
} from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useI18n } from 'vue-i18n'
import menuConfig from '@/config/menuConfig'
import axios from '@/utils/request'

export default {
  name: 'AdminSidebar',
  props: {
    collapsed: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const route = useRoute()
    const { t, locale } = useI18n()

    // 同步读取缓存：首屏渲染前决定是否显示 loading，避免「先 loading 再 logo」闪烁
    const savedLogo = typeof localStorage !== 'undefined' ? localStorage.getItem('admin_tenant_logo') : null
    const hasCachedLogo = !!savedLogo

    // 租户Logo加载状态：有缓存则不显示 loading
    const logoLoading = ref(!hasCachedLogo)
    const companyLogo = ref(hasCachedLogo ? savedLogo : null)

    // 使用公司LOGO作为页面 Favicon
    const setFavicon = (url) => {
      if (!url) return
      let link = document.querySelector('link[rel="icon"]')
      if (!link) {
        link = document.createElement('link')
        link.rel = 'icon'
        document.head.appendChild(link)
      }
      link.href = url
    }

    if (hasCachedLogo && savedLogo) {
      setFavicon(savedLogo)
    }

    // 加载租户Logo
    const loadTenantLogo = async () => {
      logoLoading.value = true
      try {
        const response = await axios.get('/tenant/info')
        if (response.code === 200 && response.data) {
          if (response.data.companyLogo) {
            companyLogo.value = response.data.companyLogo
            // 保存到localStorage，供后续使用
            localStorage.setItem('admin_tenant_logo', response.data.companyLogo)
            setFavicon(response.data.companyLogo)
          } else {
            // 如果后端没有返回，使用默认图片
            companyLogo.value = 'https://iam.jiecang.com/ac/img/jc-logo.7585b6f4.png'
            setFavicon(companyLogo.value)
          }
        } else {
          // 接口返回失败，使用默认图片
          companyLogo.value = 'https://iam.jiecang.com/ac/img/jc-logo.7585b6f4.png'
          setFavicon(companyLogo.value)
        }
      } catch (error) {
        console.error('加载租户Logo失败:', error)
        // 接口失败时，使用默认图片
        companyLogo.value = 'https://iam.jiecang.com/ac/img/jc-logo.7585b6f4.png'
        setFavicon(companyLogo.value)
      } finally {
        logoLoading.value = false
      }
    }

    const onTenantInfoUpdated = () => {
      loadTenantLogo()
    }

    onMounted(() => {
      // 有缓存时已在 setup 中同步赋值，无需再请求；无缓存时才请求
      if (!savedLogo) {
        loadTenantLogo()
      }
      // 监听系统配置保存：保存后重新拉取租户信息以更新公司 Logo
      window.addEventListener('tenant-info-updated', onTenantInfoUpdated)
    })

    onUnmounted(() => {
      window.removeEventListener('tenant-info-updated', onTenantInfoUpdated)
    })
    
    const menuItems = computed(() => menuConfig)
    
    const isActive = (path) => {
      return route.path === path || route.path.startsWith(path + '/')
    }

    const updateChecking = ref(false)
    const updateTriggering = ref(false)

    const onOneClickUpdate = async () => {
      updateChecking.value = true
      try {
        const res = await axios.get('/system/update/check')
        if (res.code !== 200 || !res.data) {
          ElMessage.warning(res.message || t('update.checkFailed'))
          return
        }
        const needUpdateMap = res.data
        const needUpdateKeys = Object.keys(needUpdateMap).filter(k => needUpdateMap[k])
        updateChecking.value = false
        if (needUpdateKeys.length === 0) {
          ElMessage.success(t('update.alreadyLatest'))
          return
        }
        const separator = locale.value === 'en-US' ? ', ' : '、'
        const names = needUpdateKeys.map(k => t(`update.${k}`)).join(separator)
        await ElMessageBox.confirm(
          t('update.confirmMessage', { list: names }),
          t('update.confirmTitle'),
          {
            confirmButtonText: t('common.confirm'),
            cancelButtonText: t('common.cancel'),
            type: 'warning'
          }
        )
        updateTriggering.value = true
        const triggerRes = await axios.post('/system/update/trigger', { needUpdate: needUpdateKeys })
        if (triggerRes.code === 200) {
          ElMessage.success(t('update.triggerSuccess'))
        } else {
          ElMessage.warning(triggerRes.message || t('update.triggerFailed'))
        }
      } catch (e) {
        if (e !== 'cancel') {
          const data = e.response?.data
          const code = data?.code
          const msg = code === 400 ? t('update.updateInProgress') : (data?.message || e.message || t('update.operationFailed'))
          if (code === 400) {
            ElMessage.warning(msg)
          } else {
            ElMessage.error(msg)
          }
        }
      } finally {
        updateChecking.value = false
        updateTriggering.value = false
      }
    }
    
    return {
      logoLoading,
      companyLogo,
      menuItems,
      isActive,
      Loading,
      Upload,
      updateChecking,
      updateTriggering,
      onOneClickUpdate
    }
  },
  components: {
    DataLine,
    Setting,
    ChatLineRound,
    FolderOpened,
    User,
    EditPen,
    PriceTag,
    Connection,
    Upload
  }
}
</script>

<style lang="scss" scoped>
.sidebar {
  position: fixed;
  left: 0;
  top: 0;
  width: 280px; //左边菜单宽度   需要和Layout.vue中一致
  height: 100vh;
  background: #001529;
  color: #fff;
  transition: width 0.3s ease;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
  
  &.collapsed {
    width: 80px;
  }
}

.sidebar-logo {
  height: 84px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  .logo-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    
    .el-icon {
      font-size: 32px;
      color: rgba(255, 255, 255, 0.8);
    }
  }
  
  .logo-img {
    width: 120px;
    height: auto;
    max-height: 80px;//设置小图标大小
    object-fit: contain;
  }
  
  .logo-icon {
    width: 80px;
    height: 80px;
    object-fit: contain;
  }
}

.sidebar-menu {
  flex: 1;
  overflow-y: auto;
  padding: 16px 0;
  
  .menu-item {
    display: flex;
    align-items: center;
    height: 48px;
    padding: 0 20px;
    color: rgba(255, 255, 255, 0.65);
    text-decoration: none;
    transition: all 0.3s;
    cursor: pointer;
    
    &:hover {
      background: rgba(255, 255, 255, 0.08);
      color: #fff;
    }
    
    &.active {
      background: #1890ff;
      color: #fff;
      border-right: 3px solid #096dd9;
    }
    
    .menu-icon {
      font-size: 18px;
      width: 24px;
      text-align: center;
      margin-right: 12px;
      flex-shrink: 0;
    }
    
    .menu-title {
      font-size: 14px;
      white-space: nowrap;
    }
  }
  
  // 折叠状态下的样式
  .sidebar.collapsed & {
    .menu-item {
      justify-content: center;
      padding: 0;
      
      .menu-icon {
        margin-right: 0;
      }
      
      .menu-title {
        display: none;
      }
    }
  }
}

// 滚动条样式
.sidebar-menu::-webkit-scrollbar {
  width: 4px;
}

.sidebar-menu::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
}

.sidebar-menu::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

.sidebar-bottom {
  flex-shrink: 0;
  padding: 12px 0 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  
  .update-btn {
    width: 100%;
    background: none;
    border: none;
    cursor: pointer;
    color: inherit;
    font: inherit;
    &:disabled {
      cursor: not-allowed;
      opacity: 0.7;
    }
  }
  
  .menu-item {
    display: flex;
    align-items: center;
    height: 48px;
    padding: 0 20px;
    color: rgba(255, 255, 255, 0.65);
    transition: all 0.3s;
    
    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.08);
      color: #fff;
    }
    
    .menu-icon {
      font-size: 18px;
      width: 24px;
      text-align: center;
      margin-right: 12px;
      flex-shrink: 0;
    }
    
    .menu-title {
      font-size: 14px;
      white-space: nowrap;
    }
  }
  
}

.sidebar.collapsed .sidebar-bottom .menu-item {
  justify-content: center;
  padding: 0;
  .menu-icon { margin-right: 0; }
  .menu-title { display: none; }
}
</style>

